import { Box, Container, Typography, Button, Grid } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import heroImage from '../assets/heroImg.jpg';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AppleIcon from "@mui/icons-material/Apple"; // Apple icon
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export default function Hero() {

  const stats = [
    "10K+ Users",
    "99.9% Uptime",
    "4.9★ Rating",
    "24/7 Support",
    "Fast & Secure",
    "Global Reach",
  ];

  const settings = {
    dots: false,
    infinite: true,
    speed: 700,
    autoplay: true,
    autoplaySpeed: 2500,
    slidesToShow: 4,
    slidesToScroll: 1,
    pauseOnHover: false,
    arrows: false,
    cssEase: "ease-in-out",
    responsive: [
      { breakpoint: 1200, settings: { slidesToShow: 3 } },
      { breakpoint: 900, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

    return (
    <>
      <Box
        id="home"
        sx={{
          pt: { xs: '100px', md: '120px' },
          pb: { xs: 6, md: 10 },
          backgroundColor: '#fff',
          minHeight: { xs: 'auto', md: '90vh' },
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Left Content */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box sx={{ maxWidth: { md: '540px' } }}>
                            {/* Tagline */}
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: '#E8F5E9',
                                    color: '#2E5E3A',
                                    px: 2.5,
                                    py: 1,
                                    borderRadius: '50px',
                                    mb: 3,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                }}
                            >
                                <BoltIcon sx={{ fontSize: '18px' }} />
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: '#2E5E3A',
                                    }}
                                >
                                    Smart expense tracking made simple
                                </Typography>
                            </Box>

                            {/* Headline */}
                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: { xs: '36px' },
                                    fontWeight: 700,
                                    lineHeight: { xs: 1.2, md: 1.1 },
                                    color: '#1a1a1a',
                                    mb: 3,
                                }}
                            >
                                Manage your expenses{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        textDecoration: 'underline',
                                        textDecorationColor: '#2E5E3A',
                                        textDecorationThickness: '3px',
                                        textUnderlineOffset: '8px',
                                    }}
                                >
                                    effortlessly
                                </Box>
                            </Typography>

                            {/* Description */}
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '16px', md: '18px' },
                                    lineHeight: 1.7,
                                    color: '#666',
                                    mb: 4,
                                    maxWidth: '520px',
                                }}
                            >
                                Track and control your spending with our intuitive expense management app. Get real-time insights and take control of your financial future.
                            </Typography>

                            {/* CTA Buttons */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                    mb: 4,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => scrollToSection('contact-us')}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                        backgroundColor: '#2E5E3A',
                                        color: '#fff',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(46, 94, 58, 0.3)',
                                        '&:hover': {
                                            backgroundColor: '#1e4a2a',
                                            boxShadow: '0 6px 16px rgba(46, 94, 58, 0.4)',
                                            transform: 'translateY(-2px)',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    Get Started
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => scrollToSection('how-this-works')}
                                    sx={{
                                        borderColor: '#2E5E3A',
                                        color: '#2E5E3A',
                                        px: 4,
                                        py: 1.5,
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        '&:hover': {
                                            borderColor: '#1e4a2a',
                                            backgroundColor: 'rgba(46, 94, 58, 0.05)',
                                            borderWidth: '2px',
                                        },
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    Learn More
                                </Button>
                            </Box>

                            {/* Feature Highlights */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: { xs: 1.5, sm: 3 },
                                    mb: 4,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#2E5E3A', fontSize: '20px' }} />
                                    <Typography
                                        sx={{
                                            fontSize: '15px',
                                            color: '#444',
                                            fontWeight: 500,
                                        }}
                                    >
                                        100% Free to start
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#2E5E3A', fontSize: '20px' }} />
                                    <Typography
                                        sx={{
                                            fontSize: '15px',
                                            color: '#444',
                                            fontWeight: 500,
                                        }}
                                    >
                                        No credit card required
                                    </Typography>
                                </Box>
                            </Box>

                            {/* App Store Links */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                }}
                            >
                                <Box
                                    component="a"
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        // Add your Google Play link here
                                    }}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#000",
                                        color: "#fff",
                                        py: 1.2,
                                        borderRadius: "10px",
                                        textDecoration: "none",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            backgroundColor: "#222",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                                        },
                                        minWidth: { xs: "100%", sm: "180px" },
                                        justifyContent: "center",
                                        gap: 1.5,
                                    }}
                                >
                                    {/* Play Store Icon */}
                                    <PlayArrowIcon />

                                    {/* Text Content */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: 0.3,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: 400,
                                                lineHeight: 1,
                                                color: "#fff",
                                            }}
                                        >
                                            GET IT ON
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "16px",
                                                fontWeight: 700,
                                                lineHeight: 1,
                                                color: "#fff",
                                            }}
                                        >
                                            Google Play
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box
                                    component="a"
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        // Add your App Store link here
                                    }}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        backgroundColor: "#000",
                                        color: "#fff",
                                        py: 1.5,
                                        borderRadius: "8px",
                                        textDecoration: "none",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            backgroundColor: "#333",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                        },
                                        minWidth: { xs: "100%", sm: "180px" },
                                        justifyContent: "center",
                                        gap: 1.5,
                                    }}
                                >
                                    {/* Apple Icon */}
                                    <AppleIcon sx={{ fontSize: 34, color: "#fff" }} />

                                    {/* Text */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: 0.5,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "10px",
                                                fontWeight: 400,
                                                lineHeight: 1,
                                                color: "#fff",
                                            }}
                                        >
                                            Download on the
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: "16px",
                                                fontWeight: 600,
                                                lineHeight: 1,
                                                color: "#fff",
                                            }}
                                        >
                                            App Store
                                        </Typography>
                                    </Box>
                                </Box>

                            </Box>
                        </Box>
                    </Grid>

            {/* Right Image */}
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  mt: { xs: 4, md: 0 },
                }}
              >
                <Box
                  component="img"
                  src={heroImage}
                  alt="People managing expenses"
                  sx={{
                    width: '100%',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    objectFit: 'cover',
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = document.createElement('div');
                    placeholder.style.cssText = `
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #2E5E3A;
                    font-size: 18px;
                    font-weight: 500;
                  `;
                    placeholder.textContent = 'Hero Image';
                    e.target.parentElement.appendChild(placeholder);
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box
        sx={{
        //   width: "100%",
          textAlign: "center",
          py: 6,
          borderRadius: "16px",
          backgroundColor: "#fff",
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            color: "#777",
            mb: 4,
          }}
        >
          Trusted by thousands of users worldwide
        </Typography>

        <Slider {...settings}>
          {stats.map((item, i) => (
            <Box key={i} sx={{ p: 2 }}>
              <Typography
                sx={{
                  fontSize: "22px",
                  fontWeight: 500,
                  color: "#b0b8c4",
                  textAlign: "center",
                }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Slider>
      </Box>
    </>
  );
}